import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { DynamicForm, SelectOption } from '../../../components/form-builder/form-builder.types';
import { MultiFormAccordionService } from '../../../components/multi-form-accordion/multi-form-accordion.service';
import { PaaRepository } from '../../../store/paa-state/paa-state.repository';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { PaaService } from '../paa.service';
import { ButtonModule } from 'primeng/button';
import { PaaMilestonesService } from '../paa-milestones.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { PaaWorkflowBlocksService } from '../paa-workflow-blocks.service';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '../../../components/form-builder/form-builder.component';
import { BehaviorSubject, of } from 'rxjs';
import { ContractingAuthorityService } from '@/pages/settings/contracting-authority/contracting-authority.service';
import { StatusComponent } from '../../../components/shared/status/status.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getPaaUpdatePlanFormObject } from '../edit-plan/utils/paa-update-form-object';
import { string } from '@/utils/string.utils';
import { CommonModule } from '@angular/common';
import { SpinnerComponent } from '../../../components/shared/spinner/spinner.component';
import { paaMilestonesStoreFactory } from '@/store/paa-state/milestones-state/milestones.store';
import { FileService, NAMESPACE_ENUM } from '@/components/shared/file-list/file-list.service';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { Paa } from '@/types';
import { fileUtils } from '@/utils/file.utils';

@Component({
  selector: 'sm-paa-inspect-plan',
  imports: [
    CommonModule,
    ButtonModule,
    PageHeaderComponent,
    FormBuilderComponent,
    StatusComponent,
    TranslateModule,
    SpinnerComponent,
    BlockBuilderComponent,
  ],
  templateUrl: './inspect-plan.component.html',
  styleUrl: './inspect-plan.component.scss',
})
export class PaaInspectPlanComponent implements OnInit, OnDestroy {
  paaService = inject(PaaService);
  paaMilestonesService = inject(PaaMilestonesService);
  paaWorkflowBlocksService = inject(PaaWorkflowBlocksService);
  paaRepository = inject(PaaRepository);
  multiFormAccordionService = inject(MultiFormAccordionService);
  contractingAuthorityService = inject(ContractingAuthorityService);
  messageService = inject(MessageService);
  fileService = inject(FileService);
  router = inject(Router);
  activatedRoute = inject(ActivatedRoute);
  translate = inject(TranslateService);

  breadcrumb = [
    {
      label: 'paa.title',
      routerLink: ['/paa-current-plans'],
    },
    {
      label: '',
      routerLink: ['paa-inspect-plan'],
    },
  ];

  id = this.activatedRoute.snapshot.params['id'] as number;
  version = this.activatedRoute.snapshot.params['version'] as number;
  loading$ = new BehaviorSubject<boolean>(true);

  acOptions: SelectOption[] = [];
  formObject: DynamicForm = getPaaUpdatePlanFormObject({ store: this.paaRepository, acOptions: this.acOptions, isUpdatable: of(false) });
  controlBlocks?: DynamicBlocks[];
  status = '';

  paa?: Paa;

  ngOnInit() {
    this.multiFormAccordionService.formFactory = (id: string) => paaMilestonesStoreFactory(id);
    this.contractingAuthorityService.getCurrentUserContractingAuthority().subscribe(data => {
      this.acOptions = data.map(ac => {
        return { name: ac.nom, code: ac.id };
      });
      this.formObject = getPaaUpdatePlanFormObject({
        store: this.paaRepository,
        acOptions: this.acOptions,
        isUpdatable: of(false),
      });
    });
    this.loadData();
    this.paaMilestonesService.initialize();
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
    this.paaRepository.reset();
  }

  downloadModel() {
    this.fileService
      .download(
        {
          filename: this.paaRepository.getNested('response', 'ref'),
          extension: 'pdf',
          alfresco_id: this.paaRepository.getNested('response', 'modelAlfrescoId'),
        },
        NAMESPACE_ENUM.PAA_MODEL
      )
      .subscribe();
  }

  downloadExcelModel() {
    if (!this.paa?.version) return;
    this.paaService
      .downloadExcelPaaModel({
        paaId: Number(this.id),
        paaVer: this.paa?.version,
      })
      .subscribe({
        next: blob => {
          const filename = `${this.paaRepository.getNested('response', 'ref')}_model.xlsx`;
          fileUtils.triggerBlobDownload(blob, filename);
        },
        error: err => {
          console.error('Download model Excel failed', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: err.error.message || this.translate.instant('Erreur lors du téléchargement du modèle Excel'),
          });
        },
      });
  }

  loadData() {
    if (this.version) {
      this.paaService.getPaaVersion(this.id, this.version).subscribe({
        next: response => {
          this.paa = response;
          this.status = response.status;
          this.multiFormAccordionService.formFactory = (id: string) => paaMilestonesStoreFactory(id);
          this.paaMilestonesService.setMilestoneDtos(response.realisations || []);
          this.paaRepository.set('response', response);
          this.paaRepository.setResponseAndDto(response);
          this.breadcrumb[1].label = `${response.ref}`;
          this.status = string.screamingSnakeToCapitalized(response.status);
          this.controlBlocks = this.paaWorkflowBlocksService.getInspectPaaBlocks({
            refreshCount: response.nbrActualisation,
            status: this.status,
            accessHistory: () => this.router.navigate(['/paa-version-history', this.id]),
            downloadSigned: () => this.downloadModel(),
            downloadExcelModel: () => this.downloadExcelModel(),
            refresh: () => this.refresh(),
          });
          this.loading$.next(false);
        },
        error: () => {
          this.loading$.next(false);
        },
      });
    } else {
      this.paaService.getPaaDetails(this.id).subscribe({
        next: response => {
          this.paa = response.paa;
          this.status = response.status;
          this.multiFormAccordionService.formFactory = (id: string) => paaMilestonesStoreFactory(id);
          this.paaMilestonesService.setMilestoneDtos(response.paa.realisations || []);
          this.paaRepository.set('response', response.paa);
          this.paaRepository.setResponseAndDto(response.paa);
          this.breadcrumb[1].label = `${response.paa.ref}`;
          this.status = string.screamingSnakeToCapitalized(response.paa.status);
          this.controlBlocks = this.paaWorkflowBlocksService.getInspectPaaBlocks({
            refreshCount: response.paa.nbrActualisation,
            status: this.status,
            accessHistory: () => this.router.navigate(['/paa-version-history', this.id]),
            downloadSigned: this.paaRepository.getNested('response', 'modelAlfrescoId') ? () => this.downloadModel() : undefined,
            downloadExcelModel: () => this.downloadExcelModel(),
            refresh: () => this.refresh(),
          });
          this.loading$.next(false);
        },
        error: () => {
          this.loading$.next(false);
        },
      });
    }
  }

  refresh() {
    this.loading$.next(true);

    this.paaService.refreshPaa(this.paaRepository.getNested('response', 'id')).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('Le Paa a été actualisé'),
        });
        this.router.navigate(['/paa-edit-plan', this.paaRepository.getNested('response', 'id')]);
        this.loading$.next(false);
      },
      error: err => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: err.error.message || this.translate.instant("Erreur lors de l'actualisation du Paa"),
        });
        this.loading$.next(false);
      },
    });
  }
}
